// react
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// hooks
import { useGoBack } from 'hooks/useGoBack';

// components
import Loading from '@karehero/llama/components/pages/Loading/Loading';
import ReferringPartnerForm from '@karehero/llama/pwa/pages/ReferringPartnerForm/ReferringPartnerForm';

// store
import {
  useCreateReferringPartnerMutation,
  useGetReferringPartnerQuery,
  useUpdateReferringPartnerMutation,
} from 'store/api/referringPartner';
import { newToast } from 'store/slices/uiSlice';
import { ROUTES } from 'router/routes';

/**
 * ReferringPartnerEditor create/edit form for a referring partner (base MUI form).
 */
export const ReferringPartnerEditor = () => {
  const dispatch = useDispatch();
  const goBack = useGoBack();
  const { partnerID } = useParams();
  const isNew = !partnerID;

  const [createPartner, { isLoading: isLoadingCreate }] =
    useCreateReferringPartnerMutation();
  const [updatePartner, { isLoading: isLoadingUpdate }] =
    useUpdateReferringPartnerMutation();
  const { data: partner, isLoading } = useGetReferringPartnerQuery(
    partnerID ?? '',
    {
      skip: isNew,
    },
  );

  const handleSubmit = useCallback(
    async (name: string, domains: string[]) => {
      try {
        if (isNew) {
          await createPartner({ name, domains }).unwrap();
          dispatch(
            newToast({
              title: 'Referring partner created',
              description: 'You can add email domains later from the list.',
              variant: 'success',
            }),
          );
        } else {
          await updatePartner({
            partnerID: partnerID!,
            body: { name, domains },
          }).unwrap();
          dispatch(
            newToast({
              title: 'Referring partner updated',
              description: 'Your changes have been saved.',
              variant: 'success',
            }),
          );
        }
        goBack();
      } catch (err) {
        dispatch(
          newToast({
            title: 'Error',
            description: isNew
              ? 'Failed to create referring partner'
              : 'Failed to update referring partner',
            variant: 'error',
          }),
        );
      }
    },
    [createPartner, updatePartner, isNew, partnerID, goBack, dispatch],
  );

  if (!isNew && isLoading) {
    return <Loading />;
  }

  return (
    <ReferringPartnerForm
      title={isNew ? 'Add Referring Partner' : 'Edit Referring Partner'}
      initialName={partner?.name}
      initialDomains={partner?.domains}
      onBack={goBack}
      onSubmit={handleSubmit}
      submitLabel={isNew ? 'Create' : 'Save'}
      isSubmitting={isLoadingCreate || isLoadingUpdate}
    />
  );
};

export default ReferringPartnerEditor;
